/**
 * **Pass 0** — where every ship is, on both of the lists she appears on, at the
 * top of a tick.
 *
 * `encounter-tick.ts`'s header is the determinism contract and says why this
 * runs first: a ship is a hull among the targets *and* a `FlakBattery` among
 * the batteries at once, and everything else on the tick aims at the picture it
 * can see, so both records are rewritten from her own motion before anything
 * looks at either. It lives here rather than in that file because that file is
 * within a few lines of aislop's size ceiling and this pass has just grown a
 * second thing to say (2026-09-01: she can now be sinking).
 *
 * ## What it does when she is going down
 *
 * `ship-damage.ts`'s `shipMotionAt` answers both regimes from one call, so
 * there is no branch here for the sinking case — only for the two consequences
 * of it that are about *lists* rather than about geometry:
 *
 * - **Her battery leaves the flak list.** A ship whose integrity is gone has no
 *   crew at her 3-pounders, and `flak.ts` has no per-battery "hold fire": it
 *   fires whatever it is given. Dropping the record is the honest way to say
 *   she has stopped, and it is what makes her bursts stop on the tick she
 *   catches rather than a tick later. `scene-ships.ts` then finds no battery for
 *   her, which is already an answer it handles — her searchlights fall to their
 *   idle sweep, and this lane puts them out entirely.
 * - **Her hull stays on the target list** until `advanceTarget` destroys it,
 *   because she is still there to be flown into, still drawn, and still the
 *   thing the renderer reads a pose off.
 *
 * ## Cheap-inert
 *
 * With no ships this returns *the arrays it was given* — the same references,
 * not copies — which is `building-strike.ts`'s idiom and the reason a sortie
 * flown without ENHANCED SIMULATION cannot tell the pass exists.
 */

import { addVec3, attitudeFromAngles, rotateVec3, type Vec3 } from "../flight/math3d.ts";
import type { FlakBattery } from "./flak-battery.ts";
import { SHIP_BATTERY_OFFSET_M } from "./ship-class.ts";
import { shipFightsOn, shipMotionAt, shipSinkElapsedS, type ShipMotion } from "./ship-damage.ts";
import type { ShipSpec } from "./ship-deployment.ts";
import { shipBatteryId, shipOfBatteryId } from "./ship-pass.ts";
import type { EncounterTarget } from "./targets.ts";

export interface ShipSteamInput {
  readonly ships: readonly ShipSpec[];
  readonly targets: readonly EncounterTarget[];
  readonly flak: readonly FlakBattery[];
  /** The encounter clock at the **start** of the tick — the pre-step time everything else aims against. */
  readonly timeS: number;
}

export interface ShipSteamResult {
  readonly targets: readonly EncounterTarget[];
  readonly flak: readonly FlakBattery[];
}

/**
 * Her position and her head as a body — the one place a `ShipMotion` becomes
 * world geometry, so a mounting offset and a hull placement cannot disagree
 * about which way she is facing.
 *
 * The quaternion carries her **head only**, as it always has. Her list and her
 * trim are on `ShipMotion` and reach the renderer through `scene-ships.ts`'s own
 * call to `shipMotionAt`, not through this: the hull's orientation is what
 * gunnery leads and what `ship-pass.ts` traverses a mounting against, and
 * neither of those wants a hull rolled twenty-five degrees into the sea.
 *
 * `sinkM` is metres below her marks, and her marks are the datum as far as the
 * sim is concerned — the renderer owns the metre the sea plate is actually
 * drawn at (`ship-design.md` §3.2, `three/ship-mounts.ts`).
 */
function bodyOfMotion(motion: ShipMotion): { position: Vec3; velocity: Vec3; orientation: ReturnType<typeof attitudeFromAngles> } {
  return {
    position: { x: motion.x, y: -motion.sinkM, z: motion.z },
    velocity: {
      x: Math.sin(motion.headingRad) * motion.speedMps,
      y: 0,
      z: -Math.cos(motion.headingRad) * motion.speedMps,
    },
    orientation: attitudeFromAngles(motion.headingRad, 0, 0),
  };
}

/** Pass 0. See the module note. */
export function steamShips(input: ShipSteamInput): ShipSteamResult {
  if (input.ships.length === 0) return { targets: input.targets, flak: input.flak };
  const hullById = new Map(input.targets.map((target) => [target.id, target]));
  const bodies = new Map<string, ReturnType<typeof bodyOfMotion>>();
  const silenced = new Set<string>();
  for (const ship of input.ships) {
    const hull = hullById.get(ship.id);
    const sinkElapsedS = hull ? shipSinkElapsedS(hull) : 0;
    bodies.set(ship.id, bodyOfMotion(shipMotionAt(ship.patrol, input.timeS, sinkElapsedS)));
    if (hull && !shipFightsOn(hull)) silenced.add(shipBatteryId(ship.id));
  }
  return {
    targets: input.targets.map((target) => {
      const body = bodies.get(target.id);
      return body ? { ...target, ...body } : target;
    }),
    flak: input.flak.flatMap((battery) => {
      if (silenced.has(battery.id)) return [];
      const body = bodies.get(shipOfBatteryId(battery.id) ?? "");
      if (!body) return [battery];
      // The same `position + rotate(orientation, offset)` `ship-pass.ts` applies
      // to a mounting, through the quaternion rather than through a heading
      // recovered from a velocity — which is zero once her way is off her.
      return [{ ...battery, position: addVec3(body.position, rotateVec3(body.orientation, SHIP_BATTERY_OFFSET_M)) }];
    }),
  };
}
